<template>
  <div v-if="model">
    <div class="columns is-variable is-5 is-multiline">
      <div class="column is-full-tablet is-half-desktop is-half-widescreen">
        <!-- Both column headers share the button's height so the two
             tables start on the same line -->
        <div class="details-head is-flex is-align-items-center mb-5">
          <h2 class="title is-5 mb-0">
            {{ $t("property_information") }}
          </h2>

          <!-- Right next to the title (no float right) -->
          <router-link
            v-if="model.can.update"
            :to="{ path: `/properties/${model.id}/edit` }"
            class="button is-primary is-small ml-4"
          >
            {{ $t("edit") }}
          </router-link>
        </div>

        <v-description>
          <v-description-item>
            <template #left>
              {{ $t("owner").capitalize() }}
            </template>

            <template #right>
              <p v-if="model.owner">
                {{ model.owner.name }}
              </p>
            </template>
          </v-description-item>
          <v-description-item>
            <template #left>
              {{ $t("owner_businesses").capitalize() }}
            </template>

            <template #right>
              <div v-if="model.owner_business">
                <b-tooltip
                  v-if="model.owner_business.billing_address"
                  type="is-black"
                  position="is-top"
                  multilined
                >
                  <p>
                    {{ model.owner_business.name }}
                  </p>
                  <template #content>
                    <p>
                      {{ model.owner_business.full_address }}
                    </p>
                  </template>
                </b-tooltip>
                <p v-else>
                  {{ model.owner_business.name }}
                </p>
              </div>
            </template>
          </v-description-item>
          <!-- Address, unit, postal code and region are in the page title
               (map + copy icons next to it) -->

          <v-description-item>
            <template #left>
              {{ $t("housing_size").capitalize() }}
            </template>

            <template #right>
              <span v-if="model.type">
                {{ $t(model.type) }}
              </span>
            </template>
          </v-description-item>

          <v-description-item>
            <template #left>
              {{ $t("inclusion_list").capitalize() }}
            </template>

            <template #right>
              <!-- Chips (design); a chip with a précision shows the (i) and
                   toggles the précision below. "(précisez)" never shows. -->
              <div
                v-if="model.inclusions"
                class="inclusion-chips py-2"
              >
                <span
                  v-for="(inclusion, index) of activeInclusions"
                  :key="index"
                  class="chip-soft"
                  :class="{ 'has-notes': hasNotes(inclusion) }"
                  :title="hasNotes(inclusion) ? $t('show_inclusion_notes') : null"
                  @click="hasNotes(inclusion) && toggleInclusionNote(inclusion.id)"
                >
                  {{ inclusionLabel(inclusion) }}
                  <i
                    v-if="hasNotes(inclusion)"
                    class="mdi mdi-information-outline"
                  />
                </span>
                <div
                  v-for="inclusion of activeInclusions.filter((i) => hasNotes(i) && shownInclusionNotes.includes(i.id))"
                  :key="'n' + inclusion.id"
                  class="inclusion-notes"
                >
                  <strong>{{ inclusionLabel(inclusion) }}</strong>
                  <div v-html="inclusion.notes" />
                </div>
              </div>
            </template>
          </v-description-item>

          <v-description-item>
            <template #left>
              {{ $t("has_laundry_room").capitalize() }}
            </template>

            <template #right>
              <p>
                {{
                  model.has_laundry_room
                    ? $t("yes").capitalize()
                    : $t("no").capitalize()
                }}
              </p>
            </template>
          </v-description-item>

          <v-description-item>
            <template #left>
              {{ $t("has_washer_and_dryer_entry").capitalize() }}
            </template>

            <template #right>
              <p>
                {{
                  model.has_washer_and_dryer_entry
                    ? $t("yes").capitalize()
                    : $t("no").capitalize()
                }}
              </p>
            </template>
          </v-description-item>
          <v-description-item>
            <template #left>
              {{ $t("square_feet_area").capitalize() }}
            </template>

            <template #right>
              {{ model.square_feet_area }}
            </template>
          </v-description-item>
          <v-description-item>
            <template #left>
              {{ $t("number_of_balconies").capitalize() }}
            </template>

            <template #right>
              {{ model.number_of_balconies }}
            </template>
          </v-description-item>
          <v-description-item>
            <template #left>
              {{ $t("floor_number").capitalize() }}
            </template>

            <template #right>
              {{ model.floor_number }}
            </template>
          </v-description-item>
          <v-description-item>
            <template #left>
              {{ $t("dog_tolerance").capitalize() }}
            </template>

            <template #right>
              {{
                model.dog_tolerance
                  ? $t("yes").capitalize()
                  : $t("no").capitalize()
              }}
            </template>
          </v-description-item>
          <v-description-item>
            <template #left>
              {{ $t("cat_tolerance").capitalize() }}
            </template>

            <template #right>
              {{
                model.cat_tolerance
                  ? $t("yes").capitalize()
                  : $t("no").capitalize()
              }}
            </template>
          </v-description-item>
          <v-description-item>
            <template #left>
              {{ $t("tolerance_notes").capitalize() }}
            </template>

            <template #right>
              <div v-html="model.tolerance_notes" />
            </template>
          </v-description-item>

          <v-description-item>
            <template #left>
              {{ $t("assurances").capitalize() }}
            </template>

            <template #right>
              {{
                model.owner.tenant_insurance
                  ? $t("yes").capitalize()
                  : $t("no").capitalize()
              }}
            </template>
          </v-description-item>
          <v-description-item>
            <template #left>
              {{ $t("modality").capitalize() }}
            </template>

            <template #right>
              {{ model.owner.payment_method }}
            </template>
          </v-description-item>
        </v-description>
      </div>
      <div
        v-if="model.latest_status"
        class="column is-full-tablet is-half-desktop is-half-widescreen"
      >
        <div class="details-head is-flex is-align-items-center mb-5">
          <h2 class="title is-5 mr-5 mb-0">
            {{ $t("current_status_information") }}
          </h2>
        </div>

        <!-- Status tags + pencil now live in the table's first row -->
        <show-property-status-details
          :status="model.latest_status"
          :property="model"
          :mode="propertyStatusMode"
          @fetch="onSuccessUpdateField"
          @open-status-modal="openModal(model)"
        />
        <b-modal
          v-model="isShownUpdateStatusModal"
          v-bind="modalPropertyStatus"
          :width="600"
          @close="closeModal"
        >
          <div class="modal-card">
            <header
              class="modal-card-head is-flex-direction-column is-flex is-align-items-flex-start"
            >
              <div
                class="is-flex is-full is-align-content-space-between"
                style="width: 100%"
              >
                <p class="modal-card-title title mb-2">
                  {{ $t("modal_status_update_in_property_list") }}
                </p>
                <button
                  class="delete"
                  aria-label="close"
                  @click="closeModal"
                />
              </div>
              <div class="modal-card-subtitle">
                <p class="is-size-7">
                  {{ $t("modal_message_first_part") }}
                  {{ $shortStreet(modalPropertyStatus.property.address.address_1) }}
                  {{ modalPropertyStatus.property.address.address_2 }}
                  ({{ modalPropertyStatus.property.address.city }})
                  {{ $t("modal_message_second_part") }}
                  <span class="has-text-weight-bold">
                    {{ $t(currentPropertyStatus) }}
                  </span>
                  <span v-if="newPropertyStatus">
                    {{ $t("modal_message_third_part") }}
                    {{ $t("from") }}
                    <span class="has-text-weight-bold">
                      {{ $t(currentPropertyStatus) }}
                    </span>
                    {{ $t("to") }}
                    <span class="has-text-weight-bold">
                      {{ $t(newPropertyStatus) }}
                    </span>
                    {{ $t("modal_message_forth_part") }}
                  </span>
                </p>
              </div>
            </header>
            <section class="modal-card-body">
              <v-popup-form
                :property="modalPropertyStatus.property"
                @success="onSuccess"
                @close="closeModal"
                @status="updateNewStatus"
              />
            </section>
          </div>
        </b-modal>
      </div>
    </div>
  </div>
</template>

<script>
import BaseDescriptionList from "./components/BaseDescriptionList.vue";
import BaseDescriptionListItem from "./components/BaseDescriptionListItem.vue";
import form_popup from "./_form_popup.vue";

import ShowPropertyStatusDetails from "./components/ShowPropertyStatusDetails.vue";

export default {
  components: {
    "v-description": BaseDescriptionList,
    "v-description-item": BaseDescriptionListItem,
    "v-popup-form": form_popup,
    "show-property-status-details": ShowPropertyStatusDetails,
  },
  props: {
    model: {
      default: null,
      type: Object,
    },
  },
  data() {
    return {
      shownInclusionNotes: [],
      isShownUpdateStatusModal: false,
      modalPropertyStatus: {
        property: {
          id: "",
          property_status_latest_type: "",
          address: {
            address_1: "",
            address_2: "",
            city: "",
          },
        },
      },
      currentPropertyStatus: null,
      newPropertyStatus: null,
    };
  },
  computed: {
    activeInclusions() {
      if (this.model.inclusions && this.model.inclusions.length > 0) {
        return this.model.inclusions.filter((inclusion) => inclusion.status);
      }
      return [];
    },
    showGoogleDriveLink() {
      return (
        this.model.latest_status.google_drive_url_link &&
        this.model.latest_status.google_drive_url_link.trim() != ""
      );
    },
    propertyStatusMode() {
      const latestStatus = this.model.latest_status || {};
      const { STATUS_RENTED, user, role_admin } = window.Laravel;

      if (latestStatus.type === STATUS_RENTED && user.role !== role_admin) {
        return "show";
      }

      return "update";
    },
  },
  methods: {
    // "Internet (précisez)" -> "Internet"
    inclusionLabel(inclusion) {
      return (inclusion.inclusion_label || "")
        .replace(/\s*\((précisez|specify)\)\s*/i, " ")
        .trim()
        .capitalize();
    },
    // A précision erased in the editor leaves empty HTML behind
    hasNotes(inclusion) {
      if (!inclusion.notes) {
        return false;
      }

      return inclusion.notes.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim() !== "";
    },
    toggleInclusionNote(inclusionId) {
      if (this.shownInclusionNotes.includes(inclusionId)) {
        const index = this.shownInclusionNotes.indexOf(inclusionId);
        if (index !== -1) {
          this.shownInclusionNotes.splice(index, 1);
          return;
        }
      }
      this.shownInclusionNotes.push(inclusionId);
    },
    openModal(property) {
      this.modalPropertyStatus = property.latest_status ?? {};
      this.modalPropertyStatus.property = { ...property };
      this.currentPropertyStatus = property.latest_status
        ? property.latest_status.type
        : "";
      this.modalPropertyStatus.property_id = property.id;
      this.isShownUpdateStatusModal = true;
    },
    updateNewStatus(newPropertyStatus) {
      this.newPropertyStatus = newPropertyStatus;
    },
    onSuccess(response) {
      this.$emit("fetch", response);
      this.isShownUpdateStatusModal = false;
      this.modalPropertyStatus.type = "";
    },
    closeModal() {
      this.isShownUpdateStatusModal = false;
    },
    onSuccessUpdateField(response) {
      this.$emit("fetch", response);
    },
  },
};
</script>

<style scoped>
.is-12px-size {
  font-size: 12px !important;
}
.is-button-toggle {
  cursor: pointer;
  opacity: 0.9;
}
.is-button-toggle:hover {
  text-decoration: underline;
  opacity: 1;
}
.inclusion-notes {
  background: #f7f7f7;
  margin: 5px 0 2px;
  border: solid 1px #eee;
  border-radius: 3px;
}
.details-head {
  min-height: 40px; /* = .button height */
}
</style>
